<template>
  <base-view-table
    title="Lista de arquivos de produtos"
    :pagination="pagination"
    :loading="loading"
    :table="table"
    :use-table="false"
    @search="list()"
  >
    <template #filters>
      <b-row>
        <b-col cols="12" class="mb-4">
          <label for="">Lojistas</label>
          <SelectShopkeeperWithApp
            v-model="fields.shopkeepersIds"
            propValueName="userId"
            @update:value="searchTimeOut"
          />
        </b-col>
        <b-col cols="12">
          <PeriodsFilters
            ref="periodsFilters"
            :setDatesAutomatic="false"
            :startDate.sync="fields.startDate"
            :endDate.sync="fields.endDate"
            @click.native="list()"
          />
        </b-col>
      </b-row>
    </template>
    <template #table-header-left>
      <div>
        <b-btn
          class="mr-2"
          @click="showAllDetails"
          size="sm"
          pill
          variant="primary"
        >
          <i class="far fa-eye mr-2"></i>
          Ver todos os arquivos
        </b-btn>
        <b-btn @click="hideAllDetails" size="sm" pill variant="secondary">
          <i class="fas fa-eye-slash mr-2"></i>
          Ocultar todos os arquivos
        </b-btn>
      </div>
    </template>
    <template #table>
      <b-table
        striped
        hover
        show-empty
        empty-text="Sem registros encontrados"
        :fields="table.headers"
        :items="table.records"
        :busy="loading"
      >
        <template #cell(shopkeeperName)="data">
          <span>{{ getShopkeeperFromId(data.item.construpUserId).name }}</span>
        </template>
        <template #cell(totalSize)="data">
          <span>{{ formatBytes(data.item.totalSize) }}</span>
        </template>
        <template #cell(actions)="row">
          <b-btn
            variant="primary"
            class="mr-2"
            @click="row.toggleDetails"
            size="sm"
            :title="!row.detailsShowing ? 'Ver aquivos' : 'Ocultar arquivos'"
          >
            <i class="fas fa-copy"></i>
          </b-btn>
          <b-btn
            size="sm"
            variant="warning"
            @click="
              $refs.shopkeeperDetails.toggleOpen({
                shopkeeperId: getShopkeeperFromId(row.item.construpUserId).id,
              })
            "
            title="Ver dados do lojista"
          >
            <i class="fas fa-eye"></i>
          </b-btn>
        </template>
        <template #row-details="data">
          <b-row v-if="data.item.files && data.item.files.length">
            <b-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
              v-for="(file, index) in data.item.files"
              :key="index"
            >
              <b-card>
                <b-row>
                  <b-col :title="file.originalName">
                    <h6 class="font-weight-bold c-orange limit-chars">
                      {{ file.originalName }}
                    </h6>
                  </b-col>
                  <b-col cols="12">
                    <p>
                      Tamanho do arquivo:
                      <b>{{ formatBytes(file.size) }}</b>
                    </p>
                    <p>
                      Modificado pela última vez em:
                      <b>{{ file.lastModifiedAt }}</b>
                    </p>
                    <p>
                      Upload feito em:
                      <b>{{ file.createdAt }}</b>
                    </p>
                    <p>
                      Extensão:
                      <b>{{ getFileExtension(file) }}</b>
                    </p>
                  </b-col>
                  <b-col cols="12" class="mt-2">
                    <b-button
                      variant="success"
                      @click="
                        downloadFile(
                          `${getImagesAddress}documento/${file.name}`,
                          file.originalName
                        )
                      "
                    >
                      <i :class="getFileIcon(file)" class="mr-2"></i>
                      Baixar {{ getFileExtension(file) }}
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
          <b-row v-else>
            <b-col class="text-center"> Sem arquivos </b-col>
          </b-row>
        </template>
      </b-table>
    </template>

    <ShopkeeperDetails ref="shopkeeperDetails" />
  </base-view-table>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import makeToast from 'Mixins/makeToast';

import { formatBytes, downloadFile } from 'Utils/index.js';

import BaseViewTable from 'Components/View/BaseViewTable.vue';
import PeriodsFilters from 'Components/Inputs/PeriodsFilters.vue';
import SelectShopkeeperWithApp from 'Components/Inputs/SelectShopkeeperWithApp.vue';
import ShopkeeperDetails from 'Components/Shopkeeper/ShopkeeperDetails.vue';

export default {
  name: 'SpreadsheetToImportList',
  mixins: [makeToast],
  data() {
    return {
      loading: false,
      fields: {
        shopkeepersIds: [],
        startDate: '',
        endDate: '',
        status: [],
      },
      table: {
        headers: [
          {
            key: 'index',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'id',
            label: 'Número da solicitação',
            sortable: true,
          },
          {
            key: 'shopkeeperName',
            label: 'Loja',
            sortable: true,
          },
          {
            key: 'totalOfFiles',
            label: 'Número de arquivos',
            sortable: true,
          },
          {
            key: 'totalSize',
            label: 'Tamanho total (MB)',
            sortable: true,
          },
          {
            key: 'createdAt',
            label: 'Enviado em',
            sortable: true,
          },
          {
            key: 'actions',
            label: 'Ações',
            sortable: false,
          },
        ],
        records: [],
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: 1,
        total: 0,
      },
      shopkeepers: [],
      timer: null,
    };
  },
  computed: {
    ...mapGetters('general', ['getImagesAddress']),
  },
  watch: {
    'pagination.currentPage': function () {
      this.list();
    },
  },
  mounted() {
    this.getShopkeepers();
    this.list();
  },
  methods: {
    formatBytes,
    downloadFile,
    ...mapActions('admin', ['getAllSpreadsheetsToImport']),
    ...mapActions('admin', ['getShopkeeperWithApps']),
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      this.timer = setTimeout(() => {
        this.list();
      }, 800);
    },
    list() {
      this.loading = true;

      const { shopkeepersIds, startDate, endDate, status } = this.fields;

      this.getAllSpreadsheetsToImport({
        currentPage: this.pagination.currentPage,
        shopkeepersIds,
        startDate,
        endDate,
        status,
      })
        .then((response) => {
          const { perPage, total, data } = response.data.data || {};

          this.pagination.itemsPerPage = perPage || 50;
          this.pagination.total = total || 0;

          this.$set(this.table, 'records', data || []);
        })
        .catch(() => {
          this.makeToast(
            'Erro',
            'Houve um erro ao trazer os arquivos',
            'danger'
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getFileExtension(file) {
      return file.extension || (file.originalName || '').split('.')[1];
    },
    getFileIcon(file) {
      const icons = {
        csv: 'fas fa-file-csv',
        txt: 'fas fa-file-csv',
        xls: 'fas fa-file-excel',
        xlsx: 'fas fa-file-excel',
      };

      return icons[this.getFileExtension(file)] || 'fas fa-file';
    },
    showAllDetails() {
      this.table.records.forEach((item) => {
        this.$set(item, '_showDetails', true);
      });
    },
    async getShopkeepers() {
      const response = await this.getShopkeeperWithApps();

      this.shopkeepers = response.data || [];
    },
    hideAllDetails() {
      this.table.records.forEach((item) => {
        this.$set(item, '_showDetails', false);
      });
    },
    getShopkeeperFromId(construpUserId) {
      return this.shopkeepers.find((x) => x.userId === construpUserId) || {};
    },
  },
  components: {
    BaseViewTable,
    PeriodsFilters,
    SelectShopkeeperWithApp,
    ShopkeeperDetails,
  },
};
</script>

<style scoped>
.limit-chars {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 99%;
}
</style>